import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ESRI, GOOGLE } from '@rsos/maps/constants/mapProviders';
import {
  AIR_AMBULANCE,
  OBLIQUE_IMAGERY,
  setCoordinates,
  setIsClickable,
} from '@rsos/reduxDataStore/src/features/geoSelection/geoSelectionSlice';

const useGeoSelectionMapCursor = ({ map, mapProvider, overlay }) => {
  const dispatch = useDispatch();
  const [googleClickEvt, setGoogleClickEvt] = useState(null);
  const [esriClickEvt, setEsriClickEvt] = useState(null);

  const isOblique = useSelector(s => s.geoSelection.obliqueImagery.isClickable);
  const isAirAmb = useSelector(s => s.geoSelection.airAmbulance.isClickable);

  const { mapInstance } = map || {};

  const addClickToOverlay = ({ key }) => {
    if (mapProvider === GOOGLE && mapInstance && !googleClickEvt) {
      const clickEvt = window.google.maps.event.addListenerOnce(
        mapInstance,
        'click',
        evt => {
          const { latLng } = evt;
          const coords = [+latLng.lng().toFixed(6), +latLng.lat().toFixed(6)];
          dispatch(setCoordinates({ key, coordinates: coords }));
        },
      );
      setGoogleClickEvt(clickEvt);
    } else if (mapProvider === ESRI && mapInstance && !esriClickEvt) {
      const clickEvt = mapInstance.view.on('click', evt => {
        const pt = evt.mapPoint;
        if (!pt) return;

        dispatch(
          setCoordinates({
            key,
            coordinates: [+pt.longitude.toFixed(6), +pt.latitude.toFixed(6)],
          }),
        );
      });
      setEsriClickEvt(clickEvt);
    }
  };

  // toggle cursor + click handler
  useEffect(() => {
    if (!mapInstance || !overlay) return;

    const isClickable = isOblique || isAirAmb;
    const key = isOblique ? OBLIQUE_IMAGERY : isAirAmb ? AIR_AMBULANCE : null;

    if (isClickable && key) {
      if (mapProvider === GOOGLE) {
        mapInstance.setOptions({
          draggableCursor: 'crosshair',
          clickableIcons: false,
        });
      } else {
        overlay._cursor = 'crosshair';
      }
      addClickToOverlay({ key });
    } else {
      // reset cursors
      if (mapProvider === GOOGLE) {
        mapInstance.setOptions({
          draggableCursor: 'grab',
          clickableIcons: true,
        });
      } else {
        overlay._cursor = 'grab';
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- addClickToOverlay is deliberately omitted to avoid re-registering click handlers on every render
  }, [mapInstance, mapProvider, overlay, isOblique, isAirAmb]);

  // cleanup when turning off
  useEffect(() => {
    if (!isOblique && !isAirAmb) {
      if (esriClickEvt) {
        esriClickEvt.remove();
        setEsriClickEvt(null);
      }
      if (googleClickEvt) {
        window.google.maps.event.removeListener(googleClickEvt);
        setGoogleClickEvt(null);
      }
    }
  }, [isOblique, isAirAmb, esriClickEvt, googleClickEvt]);

  // ESC to cancel
  useEffect(() => {
    const onEsc = e => {
      if (e.key === 'Escape') {
        dispatch(setIsClickable({ key: OBLIQUE_IMAGERY, isClickable: false }));
        dispatch(setIsClickable({ key: AIR_AMBULANCE, isClickable: false }));
      }
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [dispatch]);
};

export default useGeoSelectionMapCursor;
